import React, { FunctionComponent, ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import chevronDownIcon from '@assets/images/icons/expand.png'
import { useTranslation } from 'react-i18next'
import Widget from '@components/Widget/Widget'
import { SurveyContext } from '@context/SurveyContext'
import { PredictionContext } from '@context/PredictionContext'
import { PredictionData, PredictionResults } from '@models/Prediction/Prediction'
import './BarChartWidget.scss'
import { SurveyMode } from '@reducers/SurveyReducer'
import { APP_TYPE } from '@models/AppType'
import Tippy from '@tippyjs/react'
import { ToolTipBox } from '@components/ToolTip/ToolTipBox'
import { ToolTipContent } from '@components/ToolTip/ToolTipContent'
import { Icon } from '@components/Icon/Icon'
import clsx from 'clsx'

export interface OwnProps {
  /** When set, render this prediction instead of PredictionContext results (progress tooltip/modal). */
  predictionResults?: PredictionResults
}

interface ChartDatum {
  key: number | string
  name: string
  full: string
  value: number
  color: string
  icon?: string
}

type Props = OwnProps

const BarChartWidget: FunctionComponent<Props> = ({ predictionResults }: Props): ReactElement => {
  const { t, i18n } = useTranslation('BarChart')
  const currentLocale = (i18n.language ?? 'fr').substring(0, 2)
  const ref = useRef<HTMLDivElement | null>(null)
  const { mode } = useContext(SurveyContext)
  const { results, sandboxedResults } = useContext(PredictionContext)
  const [currentResults, setCurrentResults] = useState<undefined | PredictionResults>(
    predictionResults ?? results,
  )
  const [chartData, setChartData] = useState<ChartDatum[]>([])
  const [needsToCheckScrollbarHeight, setNeedsToCheckScrollbarHeight] = useState(false)

  const hasOverflowPercentage = chartData.some((d) => d.value > 70)

  // Timeout used to be able to give time to render the whole list of chart data
  // Then we can check if we need to show the chevron down icon
  useEffect(() => {
    if (!ref.current) {
      return
    }

    if (!chartData || chartData.length === 0) {
      return
    }

    setTimeout(() => {
      setNeedsToCheckScrollbarHeight(true)
    }, 1000)
  }, [ref.current, chartData])

  const hasScrollbar = useMemo(() => {
    if (!ref.current || !chartData || !needsToCheckScrollbarHeight) {
      return false
    }

    return ref.current.scrollHeight > ref.current.clientHeight
  }, [ref.current, chartData, needsToCheckScrollbarHeight])

  const formatPercent = (val: number) =>
    new Intl.NumberFormat('default', { style: 'percent', maximumFractionDigits: 2 }).format(val)

  useEffect(() => {
    if (predictionResults) {
      setCurrentResults(predictionResults)
      return
    }
    if (mode === SurveyMode.SANDBOX && sandboxedResults !== undefined) {
      setCurrentResults(sandboxedResults)
    } else {
      setCurrentResults(results)
    }
  }, [mode, results, sandboxedResults, predictionResults])

  useEffect(() => {
    const data = (currentResults?.prediction as { data?: PredictionData[] } | undefined)?.data
    setChartData(
      data?.map(
        ({ id: key, color, names: { short, full, acr }, value, icon }: PredictionData): ChartDatum => {
          return {
            key,
            name: short?.[currentLocale] ?? acr[currentLocale],
            full: full[currentLocale],
            value: value * 100,
            icon,
            color,
          }
        },
      ) ?? [],
    )
  }, [currentLocale, currentResults])

  return (
    <>
      <Widget propClassName="BarChartWidget">
        <h2 className="BarChartWidget-title">{t('title')}</h2>
        <div ref={ref} className={clsx('BarChartWidget-content', `BarChartWidget-content--${APP_TYPE}`)}>
          {chartData.map(({ key, name, icon, full, value, color }) => (
            <React.Fragment key={key + name}>
              <Tippy
                delay={200}
                duration={1000}
                animation={false}
                touch={true}
                render={(attr) => (
                  <ToolTipBox numberOfColumns={chartData.some((d) => d.icon) ? 3 : 2} {...attr}>
                    <ToolTipContent icon={icon} content={full} percentage={formatPercent(value / 100)} />
                  </ToolTipBox>
                )}>
                <div className={clsx('BarChartWidget-content-row', `BarChartWidget-content-row--${APP_TYPE}`)}>
                  <div className={clsx('BarChartWidget-content-col', 'BarChartWidget-content-col-name')}>
                    <span>{name}</span>
                    {icon && (
                      <div className="icon-container">
                        <Icon icon={icon} />
                      </div>
                    )}
                  </div>
                  <div className={clsx('BarChartWidget-content-col', 'BarChartWidget-content-col-percent')}>
                    <span
                      data-value={formatPercent(value / 100)}
                      style={{
                        width: `${value - (hasOverflowPercentage ? value * 0.25 : 0)}%`,
                        backgroundColor: color,
                      }}></span>
                  </div>
                </div>
              </Tippy>
            </React.Fragment>
          ))}
          {hasScrollbar && (
            <div className="BarChartWidget-content-scrollbar">
              <img className="VerticalChoices-toggle" src={chevronDownIcon} />
            </div>
          )}
        </div>
      </Widget>
    </>
  )
}

export default BarChartWidget
